import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import './App.css';
import GlobalStyle from './config/styles';
import { AuthProvider } from './context/AuthContext';
import { LoadingProvider } from './context/LoadingContext';
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import Chatbot from "./pages/Chatbot";
import ChangePassword from './pages/ChangePassword';
import ForgottenPassword from './pages/ForgottenPassword';
import ProtectedRoute from './components/ProtectedRoute';
import TermsApprovedRoute from './components/TermsApprovedRoute';
import Break from './pages/Break';
import LoadingIndicator from './components/LoadingIndicator';

const GA_TRACKING_ID = process.env.REACT_APP_GA4_KEY;

// Hook to track page views in Google Analytics
const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag("config", GA_TRACKING_ID, {
        page_path: location.pathname,
      });
    }
  }, [location]);
};

function App() {
  const [isBreak, setIsBreak] = useState(null);

  useEffect(() => {
    // Load GA script dynamically
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);

    // Initialize GA
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }
    window.gtag = gtag;
    
    gtag("js", new Date());
    gtag("config", GA_TRACKING_ID);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let intervalId;

    const checkBreaks = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/breaks`);
        const data = await res.json();
        if (!isMounted) return;
        setIsBreak(data.break);
      } catch (e) {
        console.error('Failed to check break status', e);
        if (!isMounted) return;
        setIsBreak(false);
      }
    };

    checkBreaks();

    // Check for breaks every 2 minutes
    intervalId = setInterval(checkBreaks, 120_000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Component to trigger GA tracking on route changes
  const PageTracker = () => {
    usePageTracking();
    return null;
  };

  let content;

  if (isBreak === null) {
    content = <LoadingIndicator />;
  } else if (isBreak) {
    content = <Break />;
  } else {
    content = (
      <Router>
        <PageTracker />
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route path="/change-password" element={<ChangePassword />} />
            <Route element={<TermsApprovedRoute />}>
              <Route path="/" element={<Landing />} />
              <Route path="/chatbot" element={<Chatbot />} />
            </Route>
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/forgotten-password" element={<ForgottenPassword />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    );
  }

  return (
    <div className="App">
      <AuthProvider>
        <GlobalStyle />
        <LoadingProvider>
          {content}
        </LoadingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
